import * as vp from 'vopay';

import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountCreationContext from './AccountCreationContext';
import { usePartnerLogin } from '../../../providers/PartnerLogin.provider';
import { useFetchBillingPackagesDetails } from '../../../hooks';
import { useModuleFeatureFlag } from '../../../providers/FeatureFlag.provider';

import MultiStepSection from './MultiStepSection';
import MultiStepReview from './MultiStepReview';

import { usePartnerAccountBusinessCase } from '../../../providers/PartnerAccountsBusinessCase.provider';
import { useHubSpotData } from '../../../providers/HubSpotData.provider';
import { useDocument } from '../../../providers/Document.provider';
import { usePartnerAccountMatrix } from '../../../providers/PartnerAccountsMatrix.provider';
import { createSubscriptionSection } from '@/hooks/useFields/useBillingPackageCreationFields';

const { MultiStep } = vp;

const AccountCreationForm: FunctionComponent<Record<never, never>> = () => {
  const [{ packageDetails }, getPackageDetails] = useFetchBillingPackagesDetails({});
  const ctx = useContext(AccountCreationContext);
  const { t } = useTranslation('translations', { keyPrefix: 'AccountCreationForm' });
  const { response } = usePartnerLogin();
  const isInsideSales = response.isInsideSales || false;
  const { getCompletionPercentage, hasError } = ctx.fields;
  const permissions = useModuleFeatureFlag('accounts');
  const [currentStep, setCurrentStep] = useState(0);
  const { response: businessCaseResponse } = usePartnerAccountBusinessCase();
  const { response: hubSpotDataResponse } = useHubSpotData();
  const { response: accountMatrixResponse } = usePartnerAccountMatrix();
  const { response: documentUploadResponse } = useDocument();
  const handleStepChange = (stepIdx: number) => {
    setCurrentStep(stepIdx);
  };

  useEffect(() => {
    if (ctx.values.packageType) {
      getPackageDetails(ctx.values.packageType);
    }
  }, [ctx.values.packageType]);

  useEffect(() => {
    if (ctx.values.minProgramFee) {
      const reserveToActivate = Math.ceil((Number(ctx.values.minProgramFee.replace(/[^0-9.-]+/g, '')) * 3) / 100) * 100;
      ctx.handleFieldChange(vp.createSyntheticEvent('reserveToActivate', reserveToActivate.toString()));
    }
  }, [ctx.values.minProgramFee]);

  useEffect(() => {
    if (packageDetails && isInsideSales) {
      if (packageDetails.PlanSettings) {
        const createSubscription = createSubscriptionSection(packageDetails.PlanSettings);
        createSubscription.forEach((section) => {
          section.fields.forEach((field) => {
            if (field.value !== undefined && field.value !== null) {
              ctx.values[field.fieldName] = field.value;
            }
          });
        });
      }

      ctx.values.billingMethod = packageDetails.BillingSettings?.BillingMethod.value || 'post paid';
      ctx.values.currency = packageDetails.BillingSettings?.Currency.value || 'CAD';
      ctx.values.invoiceDue = packageDetails.BillingSettings?.InvoiceDue.value || 'beginning';
      ctx.values.invoiceDueDate = packageDetails.BillingSettings?.InvoiceDueDate.value || '';
      ctx.values.minProgramFee = packageDetails.BillingSettings?.MinimumProgramFee.value || '';
      ctx.values.noMinProgramFeePeriod = packageDetails.BillingSettings?.NoMinimumProgramFeePeriod.value || '';
      ctx.values.noPlatformLicenseFee = packageDetails.BillingSettings?.NoPlatformLicenceFeePeriod.value || '';
      ctx.values.planType = packageDetails.BillingSettings?.PlanType.value || 'subscription';
      ctx.values.platformLicenseFee = packageDetails.BillingSettings?.PlatformLicenceFee.value || '';
      ctx.values.platformLicenseFeeFrequency = packageDetails.BillingSettings?.PlatformLicenseFeeFrequency.value || '';
      ctx.values.subAccountsEnabled = packageDetails.PackageSettings?.SubAccountPermissions === '1' || false;
      ctx.values.clientAccountsEnabled = packageDetails.PackageSettings?.ClientAccountsEnabled === '1' || false;
      ctx.values.useProgressivePricing = packageDetails.BillingSettings?.UseProgressivePricing?.value || false;
      ctx.values.blendedReturnRatesEnabled = packageDetails.BillingSettings?.BlendedReturnRatesEnabled?.value || false;
      ctx.values.maxGcmAmount = packageDetails.PackageSettings?.MaxGCMAmount || '';

      ctx.values.packageName = packageDetails.PackageSettings?.PackageName || '';
      ctx.values.setupFee = packageDetails.PackageSettings?.SetupFee || '';
      ctx.values.autoBalanceTransferEnabled =
        packageDetails.PackageSettings?.AutoBalanceTransferEnabled === '1' || false;

      ctx.values.country = packageDetails.PackageSettings?.ADRCountries_ID === '1' ? 'CA' : 'US';

      ctx.values.termInMonths = packageDetails.PackageSettings?.TermInMonths || '';
      ctx.handleFieldChange(vp.createSyntheticEvent('packageName', ctx.values.packageName));
      ctx.handleToggleChange(
        vp.createSyntheticEvent('autoBalanceTransferEnabled', ctx.values.autoBalanceTransferEnabled),
        { isEnabled: ctx.values.autoBalanceTransferEnabled, id: 'autoBalanceTransferEnabled' },
      );
    }
  }, [packageDetails]);

  const showCompanyProfileSection = (): boolean => isInsideSales && ctx.values.hubspotCompanyId !== '';

  const steps = [
    {
      label: '',
      content: (
        <MultiStepSection sectionId="companyProfile" showSubLabels={showCompanyProfileSection()} showHubSpotButton />
      ),
      showStep: () => true,
      hasError: () => hasError('companyProfile', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('companyProfile', ctx, currentStep >= idx),
    },
    {
      label: '',
      content: <MultiStepSection sectionId="companyFlowOfFunds" showSubLabels />,
      showStep: () => isInsideSales,
      hasError: () => hasError('companyFlowOfFunds', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('companyFlowOfFunds', ctx, currentStep >= idx),
    },
    {
      label: t('Steps.companyAddress', { defaultValue: 'Company Address' }),
      content: <MultiStepSection sectionId="companyAddress" />,
      showStep: () => !isInsideSales,
      hasError: () => hasError('companyAddress', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('companyAddress', ctx, currentStep >= idx),
    },
    {
      label: t('Steps.accountSettings', { defaultValue: 'Account Settings' }),
      content: <MultiStepSection sectionId="accountSettings" />,
      showStep: () => !isInsideSales,
      hasError: () => hasError('accountSettings', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('accountSettings', ctx, currentStep >= idx),
    },
    {
      label: t('Steps.billingPackage', { defaultValue: 'Billing Package' }),
      content: <MultiStepSection sectionId="billingPackage" />,
      showStep: () =>
        Boolean(!permissions.canAccessCustomServiceRequirements) && Boolean(!permissions.partnerBillsAccount),
      hasError: () => hasError('billingPackage', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('billingPackage', ctx, currentStep >= idx),
    },
    {
      label: t('Steps.packageSettings', { defaultValue: 'Package Settings' }),
      content: <MultiStepSection sectionId="packageInfo" />,
      showStep: () => isInsideSales,

      hasError: () => hasError('packageInfo', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('packageInfo', ctx, currentStep >= idx),
    },
    {
      label: t('Steps.billingSettings', { defaultValue: 'Billing Settings' }),
      content: <MultiStepSection sectionId="billingInfo" />,
      showStep: () => isInsideSales,

      hasError: () => hasError('billingInfo', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('billingInfo', ctx, currentStep >= idx),
    },
    {
      label: t('Steps.planSettings', { defaultValue: 'Plan Settings' }),
      content: <MultiStepSection sectionId="subscriptionInfo" showSubLabels />,
      showStep: () => isInsideSales,

      hasError: () => hasError('subscriptionInfo', ctx),
      percentageComplete: (idx: number) => getCompletionPercentage('subscriptionInfo', ctx, currentStep >= idx),
    },
    {
      label: t('Steps.reviewAndSubmit', { defaultValue: 'Review and Submit' }),
      content: <MultiStepReview />,
      showStep: () => true,
      hasError: () => false,
      percentageComplete: () =>
        businessCaseResponse?.success &&
        hubSpotDataResponse?.success &&
        accountMatrixResponse?.success &&
        documentUploadResponse?.success
          ? 100
          : 0,
    },
  ];

  return (
    <MultiStep
      id="create-account"
      title={t('title', { defaultValue: 'Add VoPay Account' })}
      steps={steps}
      onStepChange={handleStepChange}
      hasBackButton
    />
  );
};

export default AccountCreationForm;
